import { productRepository, ProductFilters } from '@/infrastructure/repositories';
import {
  Product,
  ProductsResponse,
  ProductResponse,
  CreateProductDto,
  UpdateProductDto,
  BrandsResponse,
  NextIdResponse,
  HttpError,
} from '@/domain/entities';

export class ProductService {
  async getProducts(filters: ProductFilters = {}): Promise<ProductsResponse> {
    try {
      return await productRepository.getAll(filters);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch products');
    }
  }

  async getProduct(sku: string): Promise<ProductResponse> {
    try {
      return await productRepository.getBySku(sku);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch product');
    }
  }

  async createProduct(product: CreateProductDto): Promise<void> {
    try {
      await productRepository.create(product);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to create product');
    }
  }

  async updateProduct(sku: string, updates: UpdateProductDto): Promise<void> {
    try {
      await productRepository.update(sku, updates);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to update product');
    }
  }

  async deleteProduct(sku: string): Promise<void> {
    try {
      await productRepository.delete(sku);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to delete product');
    }
  }

  async fetchProductBySku(sku: string): Promise<{
    data: {
      image_url: string;
      images: string[];
      sku: string;
      brand: string;
      model?: string;
      name: string;
      color: string;
      main_color?: string;
    };
  }> {
    try {
      return await productRepository.fetchBySku(sku);
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch product by SKU');
    }
  }

  async getBrands(): Promise<string[]> {
    try {
      const response = await productRepository.getBrands();
      return response.brands;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch brands');
    }
  }

  async getNextId(): Promise<number> {
    try {
      const response = await productRepository.getNextId();
      return response.nextId;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to fetch next ID');
    }
  }

  async checkProductExists(sku: string): Promise<boolean> {
    try {
      const response = await productRepository.checkExists(sku);
      return response.exists;
    } catch (error) {
      if (error instanceof HttpError) {
        throw error;
      }
      throw new Error('Failed to check product existence');
    }
  }
}

export const productService = new ProductService();

